/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React from 'react';
import { Routes, Route, Navigate, Outlet } from 'react-router-dom';
import AuthInitializer from './components/AuthInitializer';
import Sidebar from './components/Sidebar';
import LandingChatPage from './pages/LandingChatPage';
import DashboardPage from './pages/DashboardPage';
import BrandDetailPage from './pages/BrandDetailPage';
import SegmentsPage from './pages/SegmentsPage';
import PromotionsPage from './pages/PromotionsPageV2';
import InsightsPage from './pages/InsightsPage';
import ErrorPage from './pages/ErrorPage';
import NotFoundPage from './pages/NotFoundPage';

/**
 * Layout wrapper with sidebar
 * Uses theme-aware background colors via CSS variables
 */
const AppLayout: React.FC = () => {
  return (
    <div className="flex flex-col h-screen w-screen bg-background overflow-hidden">
      {/* PM Sandbox Banner */}
      <div className="bg-amber-500/60 backdrop-blur-sm text-black/80 px-4 py-1.5 text-center text-xs font-medium flex items-center justify-center gap-2 shrink-0">
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <span>PM Sandbox Environment — For internal alignment and feature development only. This is not a staging or production application.</span>
      </div>

      <div className="flex flex-1 overflow-hidden">
        <Sidebar />
        <div className="flex-1 overflow-hidden w-full">
          <div className="h-full w-full overflow-auto bg-background">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Main App component for BrandHealth 360
 */
const App = () => {
  console.log('=== App.tsx Component Loaded ===');
  return (
    <Routes>
      <Route path="/error" element={<ErrorPage />} />
      <Route path="/" element={<AuthInitializer />}>
        {/* All routes with sidebar layout */}
        <Route element={<AppLayout />}>
          <Route index element={<LandingChatPage />} />
          <Route path="chat" element={<LandingChatPage />} />
          <Route path="chat/:conversationId" element={<LandingChatPage />} />
          <Route path="chat-history" element={<Navigate to="/chat" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="dashboard/:brandId" element={<BrandDetailPage />} />
          <Route path="segments" element={<SegmentsPage />} />
          <Route path="promotions" element={<PromotionsPage />} />
          <Route path="insights" element={<InsightsPage />} />
        </Route>
        
        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
};

export default App;
